import { PMTiles, type RangeResponse, type Source } from "pmtiles";
import { mapPackById, mapPackForPoint } from "../../../../../shared/ecuador-map-packs";
import { getStoredMapPack } from "../../../../lib/offline-maps";
import { extractWaterLineFeaturesFromMvt, type LinearMapFeature } from "../../../../lib/map-linear-extractor";
import type { GeoArAccuracyWarning, GeoArStatus } from "../lab-v14/types/GeoArState";
import type { GeoArResult } from "../lab-v14/types/GeoArResult";
import { findBestPointOnLinearGeometry } from "./pointIdentifyLinearCore";

export type NearbyHydrologyQuery = {
  latitude: number;
  longitude: number;
  accuracy: number;
  heading: number;
  headingAccuracy: number | null;
  horizontalFov: number;
  offline: boolean;
  allowedPackIds?: readonly string[];
  maxResults?: number;
  maxDistanceMeters?: number | null;
};

export type NearbyHydrologyScan = {
  results: GeoArResult[];
  source: string;
  available: boolean;
  packIds: string[];
  scannedTiles: number;
  scannedFeatures: number;
  candidateFeatures: number;
};

const DEFAULT_PACK_IDS = ["azuay", "canar", "sierra-sur", "ecuador"] as const;
const MAX_DISTANCE_METERS = 3_000;
const MAX_PACKS_PER_QUERY = 2;

type CachedArchive = {
  downloadedAt: string;
  size: number;
  archive: PMTiles;
};

const archiveCache = new Map<string, CachedArchive>();
const tileFeatureCache = new Map<string, LinearMapFeature[]>();
const MAX_TILE_CACHE_ENTRIES = 96;

function rememberTileFeatures(key: string, features: LinearMapFeature[]) {
  if (tileFeatureCache.has(key)) tileFeatureCache.delete(key);
  tileFeatureCache.set(key, features);
  while (tileFeatureCache.size > MAX_TILE_CACHE_ENTRIES) {
    const oldest = tileFeatureCache.keys().next().value;
    if (!oldest) break;
    tileFeatureCache.delete(oldest);
  }
}

class BlobSource implements Source {
  constructor(private readonly blob: Blob, private readonly key: string) {}
  getKey() { return this.key; }
  async getBytes(offset: number, length: number): Promise<RangeResponse> {
    const data = await this.blob.slice(offset, offset + length).arrayBuffer();
    return { data };
  }
}

function getArchive(file: string, blob: Blob, downloadedAt: string) {
  const cached = archiveCache.get(file);
  if (cached && cached.downloadedAt === downloadedAt && cached.size === blob.size) return cached.archive;
  const archive = new PMTiles(new BlobSource(blob, `thera-near-linear:${file}:${downloadedAt}`));
  archiveCache.set(file, { downloadedAt, size: blob.size, archive });
  return archive;
}

function clampLatitude(latitude: number) {
  return Math.min(85.05112878, Math.max(-85.05112878, latitude));
}

function lonLatToTile(longitude: number, latitude: number, zoom: number) {
  const n = 2 ** zoom;
  const latRadians = (clampLatitude(latitude) * Math.PI) / 180;
  const x = Math.floor(((longitude + 180) / 360) * n);
  const y = Math.floor(((1 - Math.log(Math.tan(latRadians) + 1 / Math.cos(latRadians)) / Math.PI) / 2) * n);
  return { x: Math.max(0, Math.min(n - 1, x)), y: Math.max(0, Math.min(n - 1, y)), n };
}

function tileNeighborhood(longitude: number, latitude: number, zoom: number, radius: number) {
  const center = lonLatToTile(longitude, latitude, zoom);
  const output: Array<{ z: number; x: number; y: number }> = [];
  for (let dx = -radius; dx <= radius; dx += 1) {
    for (let dy = -radius; dy <= radius; dy += 1) {
      const x = center.x + dx;
      const y = center.y + dy;
      if (x < 0 || y < 0 || x >= center.n || y >= center.n) continue;
      output.push({ z: zoom, x, y });
    }
  }
  return output;
}

function accuracyWarnings(accuracy: number, headingAccuracy: number | null): GeoArAccuracyWarning[] {
  const warnings: GeoArAccuracyWarning[] = [];
  if (accuracy > 25) warnings.push("LOW_GPS_ACCURACY");
  if (headingAccuracy === null || headingAccuracy > 20) warnings.push("LOW_HEADING_ACCURACY");
  return warnings;
}

function featureConfidence(feature: LinearMapFeature, alignmentScore: number, proximityScore: number) {
  const sourceQuality = feature.named ? 1 : 0.78;
  return Math.min(0.97, alignmentScore * 0.58 + proximityScore * 0.32 + sourceQuality * 0.10);
}

function orderedPackIds(query: NearbyHydrologyQuery) {
  const allowed = new Set(query.allowedPackIds ?? DEFAULT_PACK_IDS);
  const preferred = mapPackForPoint(query.longitude, query.latitude);
  const ordered: string[] = [];
  if (preferred?.id && allowed.has(preferred.id)) ordered.push(preferred.id);
  for (const packId of query.allowedPackIds ?? DEFAULT_PACK_IDS) {
    if (allowed.has(packId) && !ordered.includes(packId)) ordered.push(packId);
  }
  return ordered;
}

async function scanPack(packId: string, query: NearbyHydrologyQuery) {
  const pack = mapPackById(packId);
  if (!pack) return null;
  const stored = await getStoredMapPack(pack.file).catch(() => undefined);
  if (!stored?.blob?.size) return null;

  const archive = getArchive(pack.file, stored.blob, stored.downloadedAt);
  const header = await archive.getHeader();
  const zoom = Math.max(header.minZoom, Math.min(header.maxZoom, 15));
  // z15 + 3x3 is intentionally local and cheap. At Ecuador latitudes it covers
  // the near-field where V1.3 should beat label points; V1.2 remains the wider fallback.
  const radius = zoom >= 15 ? 1 : zoom >= 14 ? 1 : 2;
  const tiles = tileNeighborhood(query.longitude, query.latitude, zoom, radius);
  const warnings = accuracyWarnings(query.accuracy, query.headingAccuracy);
  const maxDistanceMeters = query.maxDistanceMeters == null
    ? MAX_DISTANCE_METERS
    : Math.max(250, Math.min(MAX_DISTANCE_METERS, query.maxDistanceMeters));
  const statusBase: GeoArStatus[] = query.offline ? ["VISIBLE", ...warnings, "OFFLINE_AVAILABLE"] : ["VISIBLE", ...warnings];
  const byFeature = new Map<string, { feature: LinearMapFeature; result: GeoArResult; score: number }>();
  let scannedTiles = 0;
  let scannedFeatures = 0;

  for (const tileCoordinate of tiles) {
    const tileKey = `${pack.file}:${stored.downloadedAt}:${tileCoordinate.z}:${tileCoordinate.x}:${tileCoordinate.y}`;
    let features = tileFeatureCache.get(tileKey);
    if (!features) {
      const tile = await archive.getZxy(tileCoordinate.z, tileCoordinate.x, tileCoordinate.y).catch(() => undefined);
      if (!tile?.data?.byteLength) continue;
      features = await extractWaterLineFeaturesFromMvt(
        tile.data,
        tileCoordinate.x,
        tileCoordinate.y,
        tileCoordinate.z,
        pack.id,
      ).catch(() => [] as LinearMapFeature[]);
      rememberTileFeatures(tileKey, features);
    }
    scannedTiles += 1;
    scannedFeatures += features.length;

    for (const feature of features) {
      const aim = findBestPointOnLinearGeometry(feature.lines, {
        latitude: query.latitude,
        longitude: query.longitude,
        heading: query.heading,
        horizontalFov: query.horizontalFov,
        maxDistance: maxDistanceMeters,
      });
      if (!aim || aim.proximityScore <= 0) continue;
      const confidence = featureConfidence(feature, aim.alignmentScore, aim.proximityScore);
      const visibilityState = Math.abs(aim.relativeAngle) <= query.horizontalFov / 2 ? "VISIBLE" : "OUTSIDE_VIEW";
      const result: GeoArResult = {
        feature_uuid: feature.featureId,
        name: feature.name,
        feature_type: "RIVER",
        distance: aim.distance,
        bearing: aim.bearing,
        relative_angle: aim.relativeAngle,
        elevation_angle: null,
        relative_vertical_angle: null,
        visibility_state: visibilityState,
        confidence,
        candidate_rank: null,
        alignment_warning: false,
        accuracy_warning: [...warnings],
        data_source: `THERA Map Geometry · ${pack.shortName} PMTiles`,
        offline: query.offline,
        status: visibilityState === "VISIBLE" ? [...statusBase] : [visibilityState, ...warnings],
      };
      const current = byFeature.get(feature.key);
      if (!current || confidence > current.score || (confidence === current.score && aim.distance < (current.result.distance ?? Infinity))) {
        byFeature.set(feature.key, { feature, result, score: confidence });
      }
    }
  }

  const results = [...byFeature.values()]
    .sort((left, right) => right.score - left.score
      || Math.abs(left.result.relative_angle ?? Infinity) - Math.abs(right.result.relative_angle ?? Infinity)
      || (left.result.distance ?? Infinity) - (right.result.distance ?? Infinity))
    .slice(0, Math.max(1, Math.min(12, query.maxResults ?? 8)))
    .map(({ result }, index) => ({ ...result, candidate_rank: index + 1 }));

  return { packId: pack.id, results, scannedTiles, scannedFeatures };
}

export async function scanNearbyHydrology(query: NearbyHydrologyQuery): Promise<NearbyHydrologyScan> {
  if (typeof indexedDB === "undefined") {
    return { results: [], source: "THERA Map Geometry", available: false, packIds: [], scannedTiles: 0, scannedFeatures: 0, candidateFeatures: 0 };
  }

  const scans: Awaited<ReturnType<typeof scanPack>>[] = [];
  for (const packId of orderedPackIds(query)) {
    const scan = await scanPack(packId, query).catch(() => null);
    if (!scan) continue;
    scans.push(scan);
    if (scans.length >= MAX_PACKS_PER_QUERY) break;
    // If the most specific pack already supplies geometry candidates, avoid
    // reading another overlapping archive unless needed.
    if (scan.results.length) break;
  }

  const deduped = new Map<string, GeoArResult>();
  for (const scan of scans) {
    if (!scan) continue;
    for (const result of scan.results) {
      const key = `${String(result.name).toLocaleLowerCase("es")}|${Math.round(result.bearing ?? 0)}|${Math.round((result.distance ?? 0) / 25)}`;
      const current = deduped.get(key);
      if (!current || result.confidence > current.confidence) deduped.set(key, result);
    }
  }

  const results = [...deduped.values()]
    .sort((left, right) => right.confidence - left.confidence
      || Math.abs(left.relative_angle ?? Infinity) - Math.abs(right.relative_angle ?? Infinity)
      || (left.distance ?? Infinity) - (right.distance ?? Infinity))
    .slice(0, Math.max(1, Math.min(12, query.maxResults ?? 8)))
    .map((result, index) => ({ ...result, candidate_rank: index + 1 }));

  return {
    results,
    source: scans.length ? "THERA Map Geometry · PMTiles local" : "THERA Map Geometry",
    available: scans.length > 0,
    packIds: scans.flatMap((scan) => scan ? [scan.packId] : []),
    scannedTiles: scans.reduce((sum, scan) => sum + (scan?.scannedTiles ?? 0), 0),
    scannedFeatures: scans.reduce((sum, scan) => sum + (scan?.scannedFeatures ?? 0), 0),
    candidateFeatures: results.length,
  };
}
